import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { UserPreferencesService } from "../../../services/api/user-prefs.service";
import { UserPreferencesActions } from "../../actions/user-prefs.actions";
import { FailState } from "../../models/fail-state";

@Injectable()
export class UserPreferencesUpdateEffects {
	/**
	 * Persist preferences, then re-read them and put the SERVER's value in the
	 * store — not the value we sent.
	 *
	 * The re-read is the point. `PUT mpapi/user-settings` answers 204 with no
	 * body, and the backend maps the incoming settings map through a typed
	 * allowlist POJO marked `@JsonIgnoreProperties(ignoreUnknown = true)`, so a
	 * key it does not declare is **silently discarded** and the request still
	 * succeeds. Echoing `action.preferences` into the store made that
	 * indistinguishable from a real save: the UI showed the new value, and it
	 * vanished on the next page load. Schedule presets shipped that way.
	 *
	 * Reading back costs one extra GET per settings write and makes a dropped
	 * key visible immediately instead of at the next reload.
	 *
	 * If the write succeeds but the read-back fails, we fall back to the sent
	 * value — the write did happen, we just could not confirm what landed.
	 */
	update = createEffect(() =>
		this.actions.pipe(
			ofType(UserPreferencesActions.prefs.update),
			switchMap((action) =>
				this.prefService.updatePrefs(action.preferences).pipe(
					switchMap(() =>
						this.prefService.getPrefs().pipe(
							map((preferences) =>
								UserPreferencesActions.prefs.updateSuccess({ preferences })
							),
							catchError(() =>
								of(
									UserPreferencesActions.prefs.updateSuccess({
										preferences: action.preferences
									})
								)
							)
						)
					),
					catchError((error) =>
						of(UserPreferencesActions.prefs.updateFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private prefService: UserPreferencesService
	) {}
}
